/**
 * Header – PPHU Imperium
 * Design: Industrial Precision – granatowa belka, żółte logo, sticky
 * Mobile-first: CTA "Zadzwoń" zawsze widoczne
 */
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { Phone, Menu, X } from 'lucide-react';
import { companyData } from '@/theme';

const navLinks = [
  { label: 'Strona główna', href: '/' },
  { label: 'Oferta', href: '/oferta' },
  { label: 'Kontakt', href: '/kontakt' },
];

export default function Header() {
  const [location] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backgroundColor: '#1A2744',
        borderBottom: scrolled ? '1px solid rgba(245,196,0,0.25)' : '1px solid transparent',
        boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.35)' : 'none',
        transition: 'box-shadow 250ms ease, border-color 250ms ease',
      }}
    >
      <div className="container" style={{ maxWidth: '1280px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '68px' }}>

          {/* Logo */}
          <Link href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: '1.4rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#F5C400',
                lineHeight: 1,
              }}>
                IMPERIUM
              </div>
              <div style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 500,
                fontSize: '0.75rem',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'rgba(248,248,246,0.38)',
                lineHeight: 1,
              }}>
                PPHU · SŁAWNO
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav style={{ display: isMobile ? 'none' : 'flex', alignItems: 'center', gap: '2rem' }}>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: location === link.href ? '#F5C400' : 'rgba(248,248,246,0.8)',
                  textDecoration: 'none',
                  paddingBottom: '2px',
                  borderBottom: location === link.href ? '2px solid #F5C400' : '2px solid transparent',
                  transition: 'color 200ms ease, border-color 200ms ease',
                }}
                onMouseEnter={(e) => {
                  if (location !== link.href) {
                    (e.currentTarget as HTMLElement).style.color = '#F5C400';
                    (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(245,196,0,0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location !== link.href) {
                    (e.currentTarget as HTMLElement).style.color = 'rgba(248,248,246,0.8)';
                    (e.currentTarget as HTMLElement).style.borderBottomColor = 'transparent';
                  }
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Phone – always visible */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* CTA Phone – only on desktop */}
            {!isMobile && (
              <a
                href={`tel:${companyData.phoneRaw}`}
                className="btn-primary"
                style={{ padding: '0.6rem 1.25rem', fontSize: '0.875rem' }}
                aria-label={`Zadzwoń do PPHU Imperium: ${companyData.phone}`}
              >
                <Phone size={16} />
                {companyData.phone}
              </a>
            )}

            {/* Mobile hamburger – only on mobile */}
            {isMobile && (
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label={menuOpen ? 'Zamknij menu' : 'Otwórz menu'}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#F8F8F6',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                {menuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{
          backgroundColor: '#111B30',
          borderTop: '1px solid rgba(245,196,0,0.2)',
          padding: '1rem 0',
          animation: 'fadeSlideDown 200ms cubic-bezier(0.23, 1, 0.32, 1)',
        }}>
          <style>{`
            @keyframes fadeSlideDown {
              from { opacity: 0; transform: translateY(-8px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          <div className="container">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                style={{
                  display: 'block',
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: location === link.href ? '#F5C400' : '#F8F8F6',
                  textDecoration: 'none',
                  padding: '0.75rem 0',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {link.label}
              </Link>
            ))}
            <div style={{ marginTop: '1rem' }}>
              <a
                href={`tel:${companyData.phoneRaw}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: 'rgba(248,248,246,0.6)',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                <Phone size={14} />
                {companyData.phone}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
